// src/pages/About.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Calendar,
    Award,
    Film,
    Camera,
    Monitor,
    Cpu,
    BookOpen,
    Briefcase,
    Code,
    Layers,
    ArrowRight,
    Zap,
    Database,
    Clock,
    Target
} from 'lucide-react';
import SEOLayout from '../components/layout/SEOLayout';
import AncientModernBridge from '../components/AncientModernBridge';

const About = () => {
    const [bioData, setBioData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBioData = async () => {
            try {
                const response = await fetch('/api/content/bio');
                const data = await response.json();
                setBioData(data);
            } catch (error) {
                console.error('Error loading bio data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBioData();
    }, []);

    const iconMap = {
        Calendar, Award, Film, Camera, Monitor, Cpu, BookOpen, Briefcase, Code, Layers, Database, Clock, Target
    };

    if (loading) {
        return (
            <SEOLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-900">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
                </div>
            </SEOLayout>
        );
    }

    return (
        <SEOLayout
            title="Our Story"
            description={bioData?.company_story?.overview}
            keywords="Baraqu LLC history, Sumerian innovation, systematic technology, company story"
        >
            {/* Hero Section */}
            <section className="relative bg-gray-900 overflow-hidden" aria-labelledby="about-heading">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

                    <div className="text-center">

                        <div className="space-y-6">
                            <h1 className="text-4xl font-medium text-white mb-6">About</h1>
                            <h3 className="text-2xl font-medium text-white mb-6">Ancient Principles, Modern Applications</h3>
                            {bioData?.sumerian_connection?.parallels?.map((parallel, index) => (
                                <div key={index} className="bg-gray-800 p-6 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {/* Ancient Side */}
                                        <div>
                                            <h4 className="font-medium text-blue-400 mb-1">Ancient</h4>
                                            <p className="text-gray-300">{parallel.ancient}</p>
                                        </div>

                                        {/* Modern Side */}
                                        <div>
                                            <h4 className="font-medium text-blue-400 mb-1">Modern</h4>
                                            <p className="text-gray-300">{parallel.modern}</p>
                                        </div>
                                    </div>

                                    {/* Bridge component spans full width between the sections */}
                                    <div className="mb-3">
                                        <AncientModernBridge variant="grid" />
                                    </div>

                                    <div className="border-t border-gray-700 pt-3">
                                        <p className="text-sm text-gray-400">
                                            <strong>Core Principle:</strong> {parallel.principle}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Philosophy */}
            <section className="py-20 bg-white" aria-labelledby="philosophy-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="philosophy-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                            {bioData?.philosophy?.title}
                        </h2>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <blockquote className="text-2xl md:text-3xl text-gray-600 italic text-center mb-12 leading-relaxed">
                            "{bioData?.philosophy?.quote}"
                        </blockquote>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="bg-gray-50 p-8 rounded-lg">
                                <h3 className="text-xl font-medium text-gray-900 mb-4">Foundation</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {bioData?.philosophy?.foundation}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-lg">
                                <h3 className="text-xl font-medium text-gray-900 mb-4">Methodology</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {bioData?.philosophy?.methodology}
                                </p>
                            </div>
                            <div className="bg-gray-50 p-8 rounded-lg">
                                <h3 className="text-xl font-medium text-gray-900 mb-4">Evolution</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    {bioData?.philosophy?.evolution}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Capabilities Overview */}
            <section className="py-20 bg-gray-50" aria-labelledby="capabilities-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="capabilities-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-4">
                            {bioData?.capabilities?.title}
                        </h2>
                        <p className="text-xl text-gray-600">
                            Six specialized domains, one systematic approach
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {bioData?.capabilities?.categories?.map((capability, index) => {
                            const IconComponent = iconMap[capability.icon] || Code;
                            return (
                                <div key={index} className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 hover:border-blue-200 transition-colors duration-200">
                                    <div className="flex items-center mb-4">
                                        <div className="p-3 bg-blue-100 rounded-lg">
                                            <IconComponent className="h-8 w-8 text-blue-600" />
                                        </div>
                                        <h3 className="text-xl font-medium text-gray-900 ml-4">
                                            {capability.name}
                                        </h3>
                                    </div>
                                    <p className="text-gray-600 leading-relaxed">
                                        {capability.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Leadership & Team */}
            <section className="py-20 bg-gray-900" aria-labelledby="leadership-heading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 id="leadership-heading" className="text-3xl md:text-4xl font-light text-white mb-4">
                            Leadership & Vision
                        </h2>
                        <p className="text-xl text-gray-300">
                            Founded by technology architect Louis Silverstein
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="text-gray-300">
                            <h3 className="text-2xl font-medium text-white mb-6">Systematic Innovation Pioneer</h3>
                            <div className="space-y-4 text-lg leading-relaxed">
                                <p>
                                    Louis Silverstein founded Baraqu LLC in 2019 with a vision to apply the systematic thinking
                                    principles of ancient civilizations to modern technological challenges.
                                </p>
                                <p>
                                    With over 20 years of experience spanning VFX, hardware engineering, and software development,
                                    Louis recognized that the most enduring solutions come from methodical, systematic approaches
                                    - the same principles that made cuneiform writing last 5,000 years.
                                </p>
                                <p>
                                    Today, Baraqu's systematic methodology has delivered breakthrough solutions across industries,
                                    from healthcare simulation to entertainment technology, always with the goal of creating
                                    solutions that endure and evolve.
                                </p>
                            </div>
                        </div>

                        <div className="bg-gray-800 p-8 rounded-lg">
                            <h3 className="text-xl font-medium text-white mb-6">Key Achievements</h3>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300">PGA Innovation Award for immersive storytelling technology</p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300">USC Viterbi Visiting Innovation Fellow</p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300">50+ systematic solutions delivered across 12+ industries</p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-300">3 patent-pending technologies in development</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-white" aria-labelledby="cta-heading">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center mb-6">
                        <Zap className="h-12 w-12 text-blue-600" />
                    </div>
                    <h2 id="cta-heading" className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                        Ready to Apply 5,000 Years of Innovation?
                    </h2>
                    <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                        Let's explore how systematic thinking can transform your challenges into enduring solutions.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/contact"
                            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                            Start a Conversation
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                        <Link
                            to="/portfolio"
                            className="inline-flex items-center px-8 py-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors duration-200"
                        >
                            Explore Solutions
                        </Link>
                    </div>
                </div>
            </section>
        </SEOLayout>
    );
};

export default About;